import { useEffect, useState, MouseEvent } from "react";
import { Flex, Box, Text } from "rebass/styled-components";
import styled from "styled-components";
import { useSetRecoilState } from "recoil";
import { BiX } from "react-icons/bi";
import { SiFaceit } from "react-icons/si";

import { useUser } from "MVVM/Hooks/useUser";
import { useLinkedAccounts } from "MVVM/Hooks/useLinkedAccounts";
import { useShouldShowSubscriptionOverhaul } from "MVVM/Hooks/useSubscriptionOverhaul";
import { useResolvedPaymentProvider } from "MVVM/Hooks/useResolvedPaymentProvider";
import { StripePaystationState } from "MVVM/State";
import { ReactComponent as ProIconSVG } from "MVVM/Assets/Avatar/Icons/pro.svg";
import { ReactComponent as PlatinumIconSVG } from "MVVM/Assets/Avatar/Icons/platinum.svg";
import { ReactComponent as EssentialsIconSVG } from "MVVM/Assets/Avatar/Icons/essentials.svg";
import {
  TIER_PRICING,
  ANNUAL_STRIPE_LOOKUP,
  FACEIT_PREMIUM_COUPON,
  FACEIT_PREMIUM_DISCOUNT_PERCENT,
  getTierAccentColor,
  getTierButtonGradient,
  getTierHeaderGradient,
  getTierBorderColor,
  getTierGlowColor,
} from "MVVM/Utilities/SubscriptionTiers";

// Unlimited only: the tiers below it get the UnlimitedUpgradeUpsell
// instead, and annual-locking them at a lower tier would take them out
// of the upgrade pool for a year.
const ELIGIBLE_TYPES = ["CREATOR_PLAT"];
const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;
const SIXTY_DAYS_MS = 60 * 24 * 60 * 60 * 1000;
const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;
const LOCAL_STORAGE_KEY = "annual-upsell-seen";
const TIMER_STORAGE_KEY = "annual-upsell-timer-expiry";

const getTierIcon = (type: string) => {
  switch (type) {
    case "ESSENTIALS":
      return <EssentialsIconSVG />;
    case "PRO":
      return <ProIconSVG />;
    case "CREATOR_PLAT":
      return <PlatinumIconSVG />;
    default:
      return null;
  }
};

const formatCountdown = (ms: number): string => {
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
    2,
    "0",
  )}:${String(seconds).padStart(2, "0")}`;
};

const AnnualUpsellModal = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [timeLeft, setTimeLeft] = useState<number>(TWENTY_FOUR_HOURS_MS);
  const { allstarUser } = useUser();
  const { hasFaceitPremiumDiscount } = useLinkedAccounts();
  const setPaystation = useSetRecoilState(StripePaystationState);
  const { provider, resolve: resolveProvider } = useResolvedPaymentProvider();
  const showOverhaul = useShouldShowSubscriptionOverhaul();

  // Trigger provider resolution early so we know whether to suppress the upsell.
  useEffect(() => {
    resolveProvider();
  }, [resolveProvider]);

  const userGroup = allstarUser?.user?.userGroup;
  const loggedIn = allstarUser?.loggedIn;
  const existingProvider = allstarUser?.user?.credits?.providerName;
  // Suppress for Xsolla: winback/annual upsell pricing & flow are Stripe-only.
  const isXsollaUser = existingProvider === "XSOLLA" || provider === "xsolla";

  useEffect(() => {
    if (!showOverhaul || !loggedIn || !userGroup) return;
    if (isXsollaUser) return;
    const { active, type, paymentDate, expires } = userGroup;

    if (localStorage.getItem(LOCAL_STORAGE_KEY)) return;
    if (!active) return;
    if (!ELIGIBLE_TYPES.includes(type)) return;
    if (!paymentDate || !expires) return;

    const paymentMs = Number(paymentDate);
    const expiresMs = Number(expires);

    if (isNaN(paymentMs) || isNaN(expiresMs)) return;
    if (expiresMs - paymentMs >= SIXTY_DAYS_MS) return;
    if (Date.now() - paymentMs < SEVEN_DAYS_MS) return;

    setIsOpen(true);
  }, [showOverhaul, loggedIn, userGroup, isXsollaUser]);

  useEffect(() => {
    if (!isOpen) return;

    let expiry = Number(localStorage.getItem(TIMER_STORAGE_KEY));
    if (!expiry || isNaN(expiry)) {
      expiry = Date.now() + TWENTY_FOUR_HOURS_MS;
      localStorage.setItem(TIMER_STORAGE_KEY, String(expiry));
    }

    const tick = () => setTimeLeft(Math.max(0, expiry - Date.now()));
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [isOpen]);

  if (!isOpen || !userGroup) return <></>;

  const type = userGroup.type;
  const pricing = TIER_PRICING[type];
  const accentColor = getTierAccentColor(type);

  const handleDismiss = () => {
    localStorage.setItem(LOCAL_STORAGE_KEY, "true");
    setIsOpen(false);
  };

  const handleSwitchToAnnual = () => {
    localStorage.setItem(LOCAL_STORAGE_KEY, "true");
    setIsOpen(false);
    setPaystation((prev) => ({
      ...prev,
      isModalOpen: true,
      type: "subscribe",
      lookUp: ANNUAL_STRIPE_LOOKUP[type],
      ...(hasFaceitPremiumDiscount ? { couponId: FACEIT_PREMIUM_COUPON } : {}),
    }));
  };

  const countdown = formatCountdown(timeLeft);
  const [countHours, countMinutes, countSeconds] = countdown.split(":");

  return (
    <ModalOverlay onClick={handleDismiss}>
      <ModalContainer
        $borderColor={getTierBorderColor(type)}
        $glowColor={getTierGlowColor(type)}
        onClick={(e: MouseEvent) => e.stopPropagation()}
      >
        <CloseButton onClick={handleDismiss}>
          <BiX size={24} />
        </CloseButton>
        <CardHeader $gradient={getTierHeaderGradient(type)}>
          <BackgroundBadgeWrapper>{getTierIcon(type)}</BackgroundBadgeWrapper>
          <LimitedOfferBadge $color={accentColor}>
            Limited Offer
          </LimitedOfferBadge>
          <Title>Switch to Annual &amp; Save</Title>
          <CountdownRow>
            <CountdownLabel>Offer expires in</CountdownLabel>
            <CountdownBlocks>
              <TimeBlock $color={accentColor}>
                <TimeValue>{countHours}</TimeValue>
                <TimeUnit>HRS</TimeUnit>
              </TimeBlock>
              <TimeSeparator>:</TimeSeparator>
              <TimeBlock $color={accentColor}>
                <TimeValue>{countMinutes}</TimeValue>
                <TimeUnit>MIN</TimeUnit>
              </TimeBlock>
              <TimeSeparator>:</TimeSeparator>
              <TimeBlock $color={accentColor}>
                <TimeValue>{countSeconds}</TimeValue>
                <TimeUnit>SEC</TimeUnit>
              </TimeBlock>
            </CountdownBlocks>
          </CountdownRow>
        </CardHeader>
        <ModalContent>
          <PricingComparison>
            <PricingColumn>
              <PricingColumnHeader>Current</PricingColumnHeader>
              <PricingRow>
                <PricingPrice>{pricing.monthly}</PricingPrice>
                <PricingPeriod>/month</PricingPeriod>
              </PricingRow>
              <PricingRow>
                <PricingSecondaryPrice>
                  {pricing.monthlyAnnual}
                </PricingSecondaryPrice>
                <PricingPeriod>/year</PricingPeriod>
              </PricingRow>
            </PricingColumn>
            <PricingDivider />
            <PricingColumn $isAnnual>
              <PricingColumnHeader $color={accentColor}>
                Annual
              </PricingColumnHeader>
              <PricingRow>
                <PricingPrice $highlighted>{pricing.annual}</PricingPrice>
                <PricingPeriod>/month</PricingPeriod>
                <DiscountTag $color={accentColor}>
                  -{pricing.discount}
                </DiscountTag>
              </PricingRow>
              <PricingRow>
                <PricingSecondaryPrice $highlighted>
                  {pricing.annualTotal}
                </PricingSecondaryPrice>
                <PricingPeriod>/year</PricingPeriod>
                <DiscountTag $color={accentColor}>
                  -{pricing.discount}
                </DiscountTag>
              </PricingRow>
            </PricingColumn>
          </PricingComparison>
          {hasFaceitPremiumDiscount && (
            <FaceitDiscountBanner>
              <FaceitBadgeIcon />
              <FaceitBadgeText>
                FACEIT Premium members save an extra{" "}
                {FACEIT_PREMIUM_DISCOUNT_PERCENT}%
              </FaceitBadgeText>
            </FaceitDiscountBanner>
          )}
          <ButtonGroup>
            <ActionButton
              $gradient={getTierButtonGradient(type)}
              onClick={handleSwitchToAnnual}
            >
              Switch to Annual
            </ActionButton>
            <SecondaryButton onClick={handleDismiss}>
              Maybe later
            </SecondaryButton>
          </ButtonGroup>
        </ModalContent>
      </ModalContainer>
    </ModalOverlay>
  );
};

const ModalOverlay = styled(Box)`
  align-items: center;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  height: 100%;
  justify-content: center;
  left: 0;
  position: fixed;
  top: 0;
  width: 100%;
  z-index: 1000;
`;

const ModalContainer = styled(Box)<{
  $borderColor: string;
  $glowColor: string;
}>`
  background: ${({ theme }) => theme.colors.ned};
  border: 1px solid ${({ $borderColor }) => $borderColor};
  border-radius: 16px;
  box-shadow:
    0 4px 12px rgba(0, 0, 0, 0.25),
    0 2px 4px rgba(0, 0, 0, 0.15),
    0 0 24px ${({ $glowColor }) => $glowColor};
  max-width: 420px;
  overflow: hidden;
  position: relative;
  width: 90%;
`;

const CardHeader = styled(Box)<{ $gradient: string }>`
  background: ${({ $gradient }) => $gradient};
  border-radius: 16px 16px 0 0;
  overflow: hidden;
  padding: 28px 24px 20px;
  position: relative;
`;

const BackgroundBadgeWrapper = styled(Box)`
  left: -45px;
  opacity: 0.15;
  position: absolute;
  top: -55px;
  z-index: 0;

  svg {
    height: 140px !important;
    -webkit-mask: linear-gradient(
      to right,
      black 0%,
      black 40%,
      transparent 85%
    );
    mask: linear-gradient(to right, black 0%, black 40%, transparent 85%);
    -webkit-mask-size: 100% 100%;
    mask-size: 100% 100%;
    position: relative;
    width: 140px !important;
    z-index: 1;
  }
`;

const LimitedOfferBadge = styled(Box)<{ $color: string }>`
  background: ${({ $color }) => `${$color}1a`};
  border: 1px solid ${({ $color }) => `${$color}55`};
  border-radius: 4px;
  color: ${({ $color }) => $color};
  display: inline-block;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 1.5px;
  margin-bottom: 10px;
  padding: 3px 8px;
  position: relative;
  text-transform: uppercase;
  z-index: 1;
`;

const CloseButton = styled.button`
  align-items: center;
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.colors.chalk};
  cursor: pointer;
  display: flex;
  justify-content: center;
  padding: 8px;
  position: absolute;
  right: 12px;
  top: 12px;
  transition: color 0.2s ease;
  z-index: 2;

  &:hover {
    color: ${({ theme }) => theme.colors.envy};
  }
`;

const Title = styled(Text)`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 24px;
  font-weight: 700;
  line-height: 1.3;
  position: relative;
  z-index: 1;
`;

const CountdownRow = styled(Flex)`
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
  position: relative;
  z-index: 1;
`;

const CountdownLabel = styled(Text)`
  color: rgba(255, 255, 255, 0.5);
  font-size: 12px;
  font-weight: 500;
`;

const CountdownBlocks = styled(Flex)`
  align-items: flex-start;
  gap: 2px;
`;

const TimeBlock = styled(Box)<{ $color: string }>`
  align-items: center;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid ${({ $color }) => `${$color}33`};
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  min-width: 38px;
  padding: 4px 4px 3px;
`;

const TimeValue = styled(Text)`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 17px;
  font-variant-numeric: tabular-nums;
  font-weight: 700;
  line-height: 1;
`;

const TimeUnit = styled(Text)`
  color: rgba(255, 255, 255, 0.35);
  font-size: 8px;
  font-weight: 600;
  letter-spacing: 0.5px;
  margin-top: 2px;
  text-transform: uppercase;
`;

const TimeSeparator = styled(Text)`
  color: rgba(255, 255, 255, 0.4);
  font-size: 17px;
  font-weight: 700;
  line-height: 1;
  padding-top: 4px;
`;

const ModalContent = styled(Flex)`
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  flex-direction: column;
  gap: 16px;
  padding: 20px 24px 24px;
`;

const PricingComparison = styled(Flex)`
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 10px;
  overflow: hidden;
`;

const PricingColumn = styled(Flex)<{ $isAnnual?: boolean }>`
  flex: 1;
  flex-direction: column;
  gap: 8px;
  padding: 14px 16px;
  ${({ $isAnnual }) => $isAnnual && `background: rgba(255, 255, 255, 0.03);`}
`;

const PricingColumnHeader = styled(Text)<{ $color?: string }>`
  color: ${({ $color, theme }) => $color ?? theme.colors.chalk};
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 1.2px;
  text-transform: uppercase;
`;

const PricingDivider = styled.div`
  background: rgba(255, 255, 255, 0.06);
  flex-shrink: 0;
  width: 1px;
`;

const PricingRow = styled(Flex)`
  align-items: baseline;
  flex-wrap: wrap;
  gap: 3px;
`;

const PricingPrice = styled(Text)<{ $highlighted?: boolean }>`
  color: ${({ $highlighted, theme }) =>
    $highlighted ? theme.colors.ice : theme.colors.chalk};
  font-size: 20px;
  font-weight: 700;
  line-height: 1;
`;

const PricingSecondaryPrice = styled(Text)<{ $highlighted?: boolean }>`
  color: ${({ $highlighted }) =>
    $highlighted ? "rgba(255, 255, 255, 0.75)" : "rgba(255, 255, 255, 0.35)"};
  font-size: 14px;
  font-weight: 600;
  line-height: 1;
`;

const PricingPeriod = styled(Text)`
  color: rgba(255, 255, 255, 0.35);
  font-size: 12px;
  font-weight: 500;
`;

const DiscountTag = styled(Text)<{ $color: string }>`
  background: ${({ $color }) => `${$color}1a`};
  border: 1px solid ${({ $color }) => `${$color}55`};
  border-radius: 4px;
  color: ${({ $color }) => $color};
  font-size: 11px;
  font-weight: 700;
  padding: 1px 5px;
`;

const ButtonGroup = styled(Flex)`
  flex-direction: column;
  gap: 8px;
`;

const FaceitDiscountBanner = styled(Flex)`
  align-items: center;
  background: #000000;
  border: 1px solid ${({ theme }) => theme.colors.faceit};
  border-radius: 8px;
  gap: 8px;
  padding: 10px 14px;
`;

const FaceitBadgeIcon = styled(SiFaceit)`
  color: ${({ theme }) => theme.colors.faceit};
  flex-shrink: 0;
  height: 18px;
  width: 18px;
`;

const FaceitBadgeText = styled(Text)`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 13px;
  font-weight: 600;
`;

const ActionButton = styled.button<{ $gradient: string }>`
  align-items: center;
  background: ${({ $gradient }) => $gradient};
  border: none;
  border-radius: 8px;
  color: white;
  cursor: pointer;
  display: flex;
  font-size: 15px;
  font-weight: 600;
  height: 40px;
  justify-content: center;
  transition: filter 0.2s ease;
  width: 100%;

  &:hover {
    filter: brightness(1.1);
  }

  &:active {
    filter: brightness(0.95);
  }
`;

const SecondaryButton = styled.button`
  align-items: center;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.chalk};
  cursor: pointer;
  display: flex;
  font-size: 15px;
  font-weight: 600;
  height: 40px;
  justify-content: center;
  transition: all 0.2s ease;
  width: 100%;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    border-color: rgba(255, 255, 255, 0.25);
    color: ${({ theme }) => theme.colors.ice};
  }
`;

export { AnnualUpsellModal };
